import { useEffect, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Avatar, Badge, Button, Checkbox, ControlRow, Input, Modal, ModalBody, ModalFooter, ModalHeader, cn } from '@vd/ui';
import { Sparkles, AlertTriangle, Circle, AtSign, Archive, Users, UserCircle, Columns3, Calendar, Tag } from 'lucide-react';
import type { Column, ProjectTicketType, ProjectViewPrefs } from '@vd/types';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { ColumnDot } from '../board/ColumnDot';
import styles from './FilterModal.module.css';

export interface FilterState {
  quickFilters: ProjectViewPrefs['quickFilters'];
  assigneeIds: string[];
  authorIds: string[];
  columnIds: string[];
  /**
   * Inclusive lower bound for ticket.createdAt. Raw string from
   * <input type="datetime-local"> ("YYYY-MM-DDTHH:mm"); empty string
   * means unset. The local-timezone-ness of datetime-local matches
   * how users think about "since yesterday at 3pm".
   */
  createdFrom?: string;
  /** Inclusive upper bound for ticket.createdAt. Same shape as `createdFrom`. */
  createdTo?: string;
  /**
   * Ticket type ids, with the literal `'none'` meaning "no type" (TOADO-411).
   *
   * Not a sentinel bolted on afterwards: on the day this ships most of a
   * workspace is untyped, so a filter that could not find those would make a
   * board look emptier than it is.
   */
  typeIds?: string[];
}

export interface FilterModalProps {
  open: boolean;
  onClose: () => void;
  value: FilterState;
  onApply: (next: FilterState) => void;
  /**
   * Project columns to render as filter checkboxes ("To Do",
   * "In Review", etc.). Each project has its own column scheme so we
   * pass them in rather than fetch — the parent already has them
   * loaded for the board render.
   */
  columns?: Column[];
  /**
   * The types this project offers, already resolved by the server (TOADO-411).
   *
   * Passed in rather than fetched for the same reason `columns` is: the parent
   * has them loaded for the board render, and a project's set is a delta on the
   * company's that only the API resolves.
   */
  ticketTypes?: ProjectTicketType[];
}

const QUICK_CHIPS: Array<{ key: keyof ProjectViewPrefs['quickFilters']; label: string; icon: ReactElement }> = [
  { key: 'hasAiActivity',  label: 'Has AI activity', icon: <Sparkles size={12} /> },
  { key: 'capturePartial', label: 'Capture partial', icon: <AlertTriangle size={12} /> },
  { key: 'unresolved',     label: 'Unresolved',      icon: <Circle size={12} /> },
  { key: 'mentionsMe',     label: 'Mentions me',     icon: <AtSign size={12} /> },
  { key: 'archived',       label: 'Archived',        icon: <Archive size={12} /> },
];

const EMPTY: FilterState = {
  quickFilters: {
    hasAiActivity: false, capturePartial: false,
    unresolved: false, mentionsMe: false, archived: false,
  },
  assigneeIds: [],
  authorIds: [],
  columnIds: [],
  createdFrom: '',
  createdTo: '',
  typeIds: [],
};

export function FilterModal({ open, onClose, value, onApply, columns = [], ticketTypes = [] }: FilterModalProps) {
  const [draft, setDraft] = useState<FilterState>(value);
  const { currentCompanyId } = useAuth();

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const { data: members = [] } = useQuery({
    queryKey: ['members', currentCompanyId],
    queryFn: () => currentCompanyId ? api.listMembers(currentCompanyId) : Promise.resolve([]),
    enabled: open && !!currentCompanyId,
  });

  const toggleChip = (key: keyof ProjectViewPrefs['quickFilters']) => {
    setDraft((d) => ({
      ...d,
      quickFilters: { ...d.quickFilters, [key]: !d.quickFilters[key] },
    }));
  };

  const toggleAssignee = (id: string) => {
    setDraft((d) => ({
      ...d,
      assigneeIds: d.assigneeIds.includes(id)
        ? d.assigneeIds.filter((x) => x !== id)
        : [...d.assigneeIds, id],
    }));
  };
  const toggleAuthor = (id: string) => {
    setDraft((d) => ({
      ...d,
      authorIds: d.authorIds.includes(id)
        ? d.authorIds.filter((x) => x !== id)
        : [...d.authorIds, id],
    }));
  };
  const toggleColumn = (id: string) => {
    setDraft((d) => ({
      ...d,
      columnIds: d.columnIds.includes(id)
        ? d.columnIds.filter((x) => x !== id)
        : [...d.columnIds, id],
    }));
  };

  const toggleType = (id: string) => {
    setDraft((d) => {
      const current = d.typeIds ?? [];
      return {
        ...d,
        typeIds: current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
      };
    });
  };

  const activeCount = Object.values(draft.quickFilters).filter(Boolean).length
    + draft.assigneeIds.length
    + draft.authorIds.length
    + draft.columnIds.length
    + (draft.createdFrom ? 1 : 0)
    + (draft.createdTo ? 1 : 0)
    + (draft.typeIds?.length ?? 0);

  return (
    <Modal open={open} onClose={onClose} width={540}>
      <ModalHeader>Filter tickets</ModalHeader>
      <ModalBody data-tour-anchor="filter.panel">
        <section className={styles.section}>
          <div className={styles.label}>Quick filters</div>
          <div className={styles.chips}>
            {QUICK_CHIPS.map((c) => (
              <button
                key={c.key}
                className={cn(styles.chip, draft.quickFilters[c.key] && styles.chipOn)}
                onClick={() => toggleChip(c.key)}
                aria-pressed={draft.quickFilters[c.key]}
              >
                {c.icon}
                <span>{c.label}</span>
              </button>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.label}>
            <Calendar size={12} /> Created {(draft.createdFrom || draft.createdTo) && <Badge variant="solid">{(draft.createdFrom ? 1 : 0) + (draft.createdTo ? 1 : 0)}</Badge>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-tertiary)' }}>
              From
              <Input
                type="datetime-local"
                value={draft.createdFrom ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, createdFrom: e.target.value }))}
                max={draft.createdTo || undefined}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-tertiary)' }}>
              To
              <Input
                type="datetime-local"
                value={draft.createdTo ?? ''}
                onChange={(e) => setDraft((d) => ({ ...d, createdTo: e.target.value }))}
                min={draft.createdFrom || undefined}
              />
            </label>
          </div>
        </section>

        {ticketTypes.length > 0 && (
          <section className={styles.section}>
            <div className={styles.label}>
              <Tag size={12} /> Type {(draft.typeIds?.length ?? 0) > 0 && <Badge variant="solid">{draft.typeIds!.length}</Badge>}
            </div>
            <div className={styles.chips}>
              {/*
                Hidden types are offered here on purpose, even though a new ticket
                cannot be given one. Hiding a type stops it being CHOSEN, never
                retroactively untypes what already carries it, so a board that hid
                one still has tickets on it that somebody needs to find.
              */}
              {ticketTypes.map((type) => {
                const on = draft.typeIds?.includes(type.id) ?? false;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className={cn(styles.chip, on && styles.chipOn)}
                    onClick={() => toggleType(type.id)}
                    aria-pressed={on}
                    title={type.companyName ? `${type.name} (${type.companyName} in this workspace)` : undefined}
                  >
                    <span>{type.name}</span>
                  </button>
                );
              })}
              <button
                type="button"
                className={cn(styles.chip, (draft.typeIds?.includes('none') ?? false) && styles.chipOn)}
                onClick={() => toggleType('none')}
                aria-pressed={draft.typeIds?.includes('none') ?? false}
              >
                <span>No type</span>
              </button>
            </div>
          </section>
        )}

        {columns.length > 0 && (
          <section className={styles.section}>
            <div className={styles.label}>
              <Columns3 size={12} /> Column {draft.columnIds.length > 0 && <Badge variant="solid">{draft.columnIds.length}</Badge>}
            </div>
            <div className={styles.chips}>
              {columns.map((c) => {
                const on = draft.columnIds.includes(c.id);
                return (
                  <button
                    key={c.id}
                    type="button"
                    className={cn(styles.chip, on && styles.chipOn)}
                    onClick={() => toggleColumn(c.id)}
                    aria-pressed={on}
                  >
                    <ColumnDot tone={c.tone} size={6} />
                    <span>{c.name}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <section className={styles.section}>
          <div className={styles.label}>
            <Users size={12} /> Assignee {draft.assigneeIds.length > 0 && <Badge variant="solid">{draft.assigneeIds.length}</Badge>}
          </div>
          <div className={styles.memberList}>
            {members.map(({ user }) => (
              <ControlRow key={user.id} className={styles.memberRow}>
                <Checkbox
                  checked={draft.assigneeIds.includes(user.id)}
                  onChange={() => toggleAssignee(user.id)}
                />
                <Avatar name={user.name} size="sm" />
                <span>{user.name}</span>
              </ControlRow>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <div className={styles.label}>
            <UserCircle size={12} /> Author {draft.authorIds.length > 0 && <Badge variant="solid">{draft.authorIds.length}</Badge>}
          </div>
          <div className={styles.memberList}>
            {members.map(({ user }) => (
              <ControlRow key={user.id} className={styles.memberRow}>
                <Checkbox
                  checked={draft.authorIds.includes(user.id)}
                  onChange={() => toggleAuthor(user.id)}
                />
                <Avatar name={user.name} size="sm" />
                <span>{user.name}</span>
              </ControlRow>
            ))}
          </div>
        </section>
      </ModalBody>
      <ModalFooter>
        <Button variant="ghost" onClick={() => setDraft(EMPTY)}>Clear all</Button>
        <div style={{ flex: 1 }} />
        <Button variant="ghost" onClick={onClose} data-tour-anchor="filter.cancel">Cancel</Button>
        <Button variant="primary" onClick={() => { onApply(draft); onClose(); }}>
          Apply {activeCount > 0 && `(${activeCount})`}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
